import React, { useRef } from 'react';
import { TrabadJuLogo } from '@/components/LogoGenerator';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download } from 'lucide-react';

export default function LogosDownload() {
  // Function to download SVG as PNG
  const downloadAsPNG = (elementId, filename, width, height) => {
    const svgElement = document.getElementById(elementId).querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = width;
    canvas.height = height;
    
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.download = filename;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(url);
      }, 'image/png', 1.0);
    };
    
    img.src = url;
  };

  const logos = [
    {
      id: 'instagram-profile',
      title: 'Instagram Perfil',
      size: '320x320px',
      width: 320,
      height: 320,
      filename: 'trabadju_instagram_profile.png',
      showSubtext: true
    },
    {
      id: 'instagram-post',
      title: 'Instagram Post',
      size: '1080x1080px',
      width: 1080,
      height: 1080,
      filename: 'trabadju_instagram_post.png',
      showSubtext: true
    },
    {
      id: 'instagram-story',
      title: 'Instagram Story',
      size: '1080x1920px',
      width: 1080,
      height: 1920,
      filename: 'trabadju_instagram_story.png',
      showSubtext: true,
      variant: 'vertical'
    },
    {
      id: 'facebook-post',
      title: 'Facebook Post',
      size: '1200x630px',
      width: 1200,
      height: 630,
      filename: 'trabadju_facebook_post.png',
      showSubtext: true,
      variant: 'wide'
    },
    {
      id: 'twitter-header',
      title: 'Twitter/X Header',
      size: '1500x500px',
      width: 1500,
      height: 500,
      filename: 'trabadju_twitter_header.png',
      showSubtext: true,
      variant: 'wide'
    },
    {
      id: 'linkedin-post',
      title: 'LinkedIn Post',
      size: '1200x627px',
      width: 1200,
      height: 627,
      filename: 'trabadju_linkedin_post.png',
      showSubtext: true,
      variant: 'wide'
    },
    {
      id: 'favicon',
      title: 'Favicon/App Icon',
      size: '512x512px',
      width: 512,
      height: 512,
      filename: 'trabadju_favicon.png',
      showSubtext: false
    },
    {
      id: 'whatsapp-profile',
      title: 'WhatsApp Perfil',
      size: '500x500px',
      width: 500,
      height: 500,
      filename: 'trabadju_whatsapp_profile.png',
      showSubtext: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Logos Trabadju para Redes Sociais
          </h1>
          <p className="text-gray-600">
            Descarrega os logos em formato PNG otimizados para cada plataforma
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {logos.map((logo) => (
            <Card key={logo.id} className="p-6">
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-900 mb-1">{logo.title}</h3>
                <p className="text-sm text-gray-500">{logo.size}</p>
              </div>

              <div 
                id={logo.id}
                className="bg-white border rounded-lg mb-4 flex items-center justify-center overflow-hidden"
                style={{ 
                  height: logo.height > logo.width ? '300px' : '200px',
                  maxWidth: '100%'
                }}
              >
                <div style={{ 
                  width: '100%', 
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrabadJuLogo 
                    width={logo.width} 
                    height={logo.height}
                    showSubtext={logo.showSubtext}
                    variant={logo.variant}
                  />
                </div>
              </div>

              <Button
                onClick={() => downloadAsPNG(logo.id, logo.filename, logo.width, logo.height)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Descarregar PNG
              </Button>
            </Card>
          ))}
        </div>

        <Card className="p-6 mt-12 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-gray-900 mb-3">📋 Guia de Utilização</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✅ <strong>Instagram Perfil:</strong> Use para foto de perfil (320x320px)</li>
            <li>✅ <strong>Instagram Post:</strong> Posts quadrados no feed (1080x1080px)</li>
            <li>✅ <strong>Instagram Story:</strong> Stories verticais (1080x1920px)</li>
            <li>✅ <strong>Facebook Post:</strong> Posts e imagens partilhadas (1200x630px)</li>
            <li>✅ <strong>Twitter/X Header:</strong> Imagem de cabeçalho (1500x500px)</li>
            <li>✅ <strong>LinkedIn Post:</strong> Imagens em posts (1200x627px)</li>
            <li>✅ <strong>Favicon:</strong> Ícone do site e app (512x512px)</li>
            <li>✅ <strong>WhatsApp:</strong> Foto de perfil business (500x500px)</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
